<script>
  import { loadStripe } from '@stripe/stripe-js';
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl, getStripeUrl } from '../api'; 
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore'; // <-- IMPORT i18n STORE

  // Use the centralized key with fallback
  const STRIPE_KEY = getStripeUrl();
  const stripePromise = loadStripe(STRIPE_KEY);

  let amount = 100; 
  let currency = 'HKD';
  let minAmount = 10; 

  let donorName = '';
  let email = '';
  let message = '';
  let isAnonymous = false;
  
  let stripe;
  let elements;
  let isLoading = false;
  let isProcessingPayment = false;
  let error = '';

  // Reactive Logic: Set Currency and Minimum Amount based on Country
  $: {
      if ($selectedCountry === 'GB') {
          currency = 'GBP';
          minAmount = 1;
      } else if ($selectedCountry === 'JP') {
          currency = 'JPY';
          minAmount = 200;
      } else {
          // Default HK
          currency = 'HKD';
          minAmount = 10;
      }
  }

  onMount(async () => {
    if (!STRIPE_KEY) {
        error = $t('donate.error_stripe_key_missing') || "Stripe key is missing in configuration.";
        return;
    }
    stripe = await stripePromise;
  });

  async function handleSubmit() {
    if (!amount || amount < minAmount) {
        error = ($t('donate.error_min_donation') || `Minimum donation for {currency} is {minAmount}`)
            .replace('{currency}', currency)
            .replace('{minAmount}', minAmount);
        return;
    }
    
    isLoading = true;
    error = '';

    try {
        const res = await fetch(getApiUrl('/donations/intent'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ donorName, email, amount, currency, message, isAnonymous })
        });
        
        const data = await res.json();
        if(!res.ok) throw new Error(data.error);

        elements = stripe.elements({ clientSecret: data.clientSecret });
        const paymentElement = elements.create('payment');
        
        await tick();
        
        paymentElement.mount('#payment-element-container');
        
    } catch(e) {
        error = e.message;
    } finally {
        isLoading = false;
    }
  }

  async function confirmPayment() {
      if (!stripe || !elements) return;

      isProcessingPayment = true;
      error = '';

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.origin + '/donations/list' },
          redirect: 'if_required'
      });

      if (stripeError) {
          error = stripeError.message;
          isProcessingPayment = false;
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          try {
            await fetch(getApiUrl('/donations/confirm'), {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ paymentIntent: paymentIntent.id })
            });
            navigate('/donations/list');
          } catch (e) {
              error = ($t('donate.error_server_confirmation') || "Payment succeeded, but server confirmation failed: {error}")
                  .replace('{error}', e.message);
              isProcessingPayment = false;
          }
      }
  }
</script>

<div class="donate-container">
    <h2>{$t('donate.title') || 'Support SNAP PERK'}</h2>
    <p>{$t('donate.subtitle') || 'Your donation helps us maintain the platform.'}</p>

    {#if !elements}
        <form on:submit|preventDefault={handleSubmit}>
            <div class="form-group">
                <label>{($t('donate.amount_label') || 'Amount ({currency})').replace('{currency}', currency)}</label>
                <div class="input-wrapper">
                    <input type="number" bind:value={amount} min={minAmount} />
                    <span class="hint">{($t('donate.min_hint') || 'Min: {minAmount}').replace('{minAmount}', minAmount)}</span>
                </div>
            </div>
            <div class="form-group">
                <label>{$t('donate.name_label') || 'Name (Optional)'}</label>
                <input type="text" bind:value={donorName} placeholder={$t('donate.name_placeholder') || 'Your Name'} />
            </div>
            <div class="form-group">
                <label>{$t('donate.email_label') || 'Email (Receipts)'}</label>
                <input type="email" bind:value={email} placeholder={$t('donate.email_placeholder') || 'you@example.com'} required />
            </div>
            <div class="form-group">
                <label>{$t('donate.message_label') || 'Message (Optional)'}</label>
                <textarea bind:value={message} rows="3"></textarea>
            </div>
            <div class="chk-group">
                <label>
                    <input type="checkbox" bind:checked={isAnonymous}> 
                    {$t('donate.hide_name') || 'Hide my name on the public list'}
                </label>
            </div>
            <button type="submit" disabled={isLoading} class="primary-btn">
                {#if isLoading}
                    {$t('donate.processing') || 'Processing...'}
                {:else}
                    {($t('donate.donate_btn') || 'Donate {currency} {amount}')
                        .replace('{currency}', currency)
                        .replace('{amount}', amount)}
                {/if}
            </button>
        </form>
    {/if}

    <div id="payment-element-container"></div>
    
    {#if elements}
        <button class="primary-btn confirm-btn" on:click={confirmPayment} disabled={isProcessingPayment}>
            {isProcessingPayment 
                ? ($t('donate.confirming') || 'Confirming...') 
                : ($t('donate.confirm_payment') || 'Confirm Payment')}
        </button>
    {/if}

    {#if error} <p class="error">{error}</p> {/if}
</div>

<style>
    .donate-container { max-width: 500px; margin: 2rem auto; padding: 2rem; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
    .form-group { margin-bottom: 1rem; text-align: left; }
    label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
    input, textarea { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
    .input-wrapper { position: relative; }
    .hint { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #888; font-size: 0.8rem; }
    .chk-group { margin-bottom: 1.5rem; text-align: left; }
    .primary-btn { width: 100%; padding: 0.8rem; background-color: #28a745; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
    .primary-btn:disabled { background-color: #aaa; cursor: not-allowed; }
    .confirm-btn { margin-top: 1.5rem; background-color: #007bff; }
    .confirm-btn:hover { background-color: #0056b3; }
    .error { color: red; margin-top: 1rem; font-weight: bold; text-align: center; }
    #payment-element-container { margin-top: 1rem; }
</style>